import { useQuery } from '@tanstack/react-query';
import http from 'helpers/requests';
import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';
import { normaliseReleases } from '../utils/releaseNotes';

// The release-notes query.
//
// CONTRACT (D2): GET /api/release-notes -> { releases: [...] }, newest first.
// The frontend ships ahead of the endpoint (D3): every surface stays invisible
// until it answers, so this can merge and deploy on its own. The backend half
// is implemented on `claude/release-notes-endpoint` in the backend repo and
// serves an empty catalogue until someone authors a note — which reaches here
// as `{"releases": []}` and is, deliberately, indistinguishable from "not
// deployed yet".
//
// LOCALE: the endpoint is content-negotiated (backend D28) — it resolves
// Accept-Language through the server's locale gate, falls back to English per
// field, and answers `Vary: Accept-Language`. Nothing here sets the header: the
// global interceptor in helpers/requests.js stamps the APP locale, and the
// locale-change handler in index.jsx clears the query cache, so the key needs no
// locale in it. useReleaseNotes.test.jsx pins both.
//
// `retry: false` is load-bearing, not a preference. The shared queryClient
// retries twice (src/queryClient.js), so against an endpoint that is not
// deployed this would be three 404s per attempt rather than one, for every
// user, for as long as the backend lags.
//
// It is one request per attempt, NOT one per session: `refetchOnMount` is left
// at the client default, and an errored query has no `dataUpdatedAt`, so each
// new observer mount retries once. That is deliberate — it is how a
// newly-deployed endpoint gets picked up without a reload — but it does mean
// the 404 recurs on remount, not just once.
//
// ORG SCOPE (role-scoped-release-notes D14): the payload is per-caller — the
// server filters the catalogue by the caller's org type and Regime — so the key
// carries the org id. This is defence in depth rather than the thing that makes
// it correct: a real org switch already clears the whole cache (OrgBoundary,
// after refreshing the Clerk token), sign-out clears it (_app.jsx) and a locale
// switch clears it (index.jsx). Correctness should not rest on three separate
// callers of `queryClient.clear()` staying correct.
//
// The org id comes from the permissions context, NOT from `useAuth()`, for the
// reason PermissionsProvider documents at length: `useAuth().orgId` lags a
// same-session `setActive()` by a beat, so keying on it churns the key AFTER a
// switch has settled and caches the new org's payload under the old org's key.
// The context echoes Clerk's authoritative imperative channel. `?? {}` covers
// rendering outside the provider (unit tests), matching useWhatsNewAutoOpen.
export const useReleaseNotes = (options = {}) => {
  const { orgId } = usePermissionsContext() ?? {};

  return useQuery({
    // `orgId || null` — an absent org is one cache entry, not one per falsy
    // shape. That entry is real and reachable: a caller with no resolvable
    // organisation is answered `200 {"releases": []}` rather than a 403
    // (backend D8), so it holds an empty catalogue rather than an error.
    queryKey: ['releaseNotes', orgId || null],
    queryFn: async () => {
      const res = await http.get('/release-notes');
      return normaliseReleases(res?.data?.releases);
    },
    // Release notes change on deploy, not on interaction — a five-minute
    // staleTime keeps the org-profile tab warm without polling.
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export default useReleaseNotes;
